import React from 'react';
import { useTranslation } from 'react-i18next';
import SeoMeta from '../components/SeoMeta';
import { useLocalizedUrl } from '../utils/urlHelper';
import { PageHero, SectionIntro, ClosingCta, CheckList, faqSchema, usePrimaryCta } from '../components/common/B2B';
import { Section, ItemsSection, FaqSection, HeroChecks, TwoColumns, RelatedSection } from '../components/common/B2BSections';
import { otherTrustPages, RELATED_TITLE } from '../components/common/trustPages';

// Confianza · Firma y evidencias (nov-2026). Rediseño B2B con el contenido de la
// antigua página de firma (signature-translations.js, sin uso) y, desde el 4-nov,
// también el de «Conservación de evidencias», que Jesús pidió unir aquí para
// acortar la web (/confianza/conservacion-evidencias → 301 a esta página).
// Sin repeticiones: el PDF PAdES-LTV y «qué se conserva» van en una sola sección
// (#conservacion); el reparto de responsabilidades, en la FAQ de plazos.
// Informe pericial: texto de Jesús (13-sep), intacto en lo sustancial.
// Hero con foto (4-nov, Jesús: «una imagen de IA de alguien firmando en el móvil»;
// la captura real de la pantalla de firma no le convenció): GPT Image 2 vía fal,
// marketing/prompts-imagenes/hero-firma-movil.txt, candidata gpt-01.
const HERO_IMAGE = { src: '/images/confianza/hero-firma-movil.webp', position: '70% 50%' };

const PAGE_URL = { es: 'https://www.bluecmr.com/confianza/firma-electronica', en: 'https://www.bluecmr.com/en/trust/electronic-signature' };

const COPY = {
  es: {
    seoTitle: 'Firma electrónica avanzada y conservación de evidencias | BlueCMR',
    seoDescription: 'Cómo se firman y conservan los documentos de transporte en BlueCMR: firma avanzada conforme a eIDAS, evidencias de cada firma y PDF en PAdES-LTV.',
    seoKeywords: 'firma electrónica avanzada, firma documentos de transporte, eidas, firma biométrica, pades-ltv, conservación documentos de transporte, evidencias electrónicas',
    heroEyebrow: 'Confianza · Firma y evidencias',
    heroTitle: 'Firmado en el móvil, demostrable años después',
    heroText: 'Cada firma en BlueCMR es una firma electrónica avanzada: vincula a quien firma con el documento y con su voluntad y deja evidencias que la prueban. El documento se conserva con todas ellas, íntegro y verificable, también fuera de la plataforma.',
    heroChecks: ['eIDAS, artículo 26', 'PAdES-LTV', 'Exportable'],
    typesEyebrow: 'Marco legal',
    typesTitle: 'La firma que usa BlueCMR',
    typesText: 'El Reglamento eIDAS (UE 910/2014) distingue tres tipos de firma electrónica. BlueCMR usa la avanzada, adecuada para los documentos de transporte conforme a eIDAS, al Protocolo eCMR y a la normativa española.',
    types: [
      { title: 'Simple', text: 'Cualquier dato electrónico que el firmante asocia a otros para firmar.' },
      { title: 'Avanzada', text: 'Vinculada al firmante de forma única, permite identificarlo, se crea bajo su control y hace detectable cualquier cambio posterior (art. 26).', mark: 'La de BlueCMR' },
      { title: 'Cualificada', text: 'Avanzada, creada con un dispositivo cualificado y basada en un certificado cualificado; equivale a la manuscrita.' },
    ],
    modes: {
      eyebrow: 'Modalidades',
      title: 'Cómo se firma',
      cols: 4,
      items: [
        { title: 'Biométrica manuscrita', text: 'Con el dedo, en el móvil o la tableta. Se capturan el trazo y su dinámica (posición, tiempo, velocidad, presión y aceleración), cifrados en el propio dispositivo.' },
        { title: 'Con sello electrónico', text: 'Para los usuarios identificados de la empresa: firma con un clic, sin trazo manuscrito.' },
        { title: 'Desatendida', text: 'Para procesos automáticos, con el sello electrónico de BlueCMR.' },
        { title: 'Con verificación OTP', text: 'Si se configura, el firmante confirma con un código que recibe por SMS o correo: evidencia añadida de que controla ese canal.' },
      ],
    },
    sendEyebrow: 'Envío para firma',
    sendTitle: 'Firman sin crear una cuenta',
    send: [
      { title: 'Quién firma', text: 'Conductores, segundos conductores, operadores de transporte, destinatarios y otros intervinientes, cada uno desde su dispositivo.', items: ['En el propio dispositivo de quien emite', 'O con el documento enviado a cada firmante', 'Sin instalar nada ni registrarse'] },
      { title: 'Cada enlace de firma', text: 'Llega por correo, SMS, WhatsApp o código QR y:', items: ['Va asociado a un token único', 'Se puede revocar en cualquier momento', 'Solo permite la firma autorizada'] },
    ],
    evidence: {
      eyebrow: 'Evidencias',
      title: 'Lo que queda de cada firma',
      text: 'Cada firma genera un conjunto de evidencias que permite reconstruirla y demostrarla ante terceros, autoridades o en un procedimiento judicial.',
      items: [
        { title: 'Identidad del firmante', text: 'Nombre, correo y documento de identidad, con constancia de si vienen del perfil de la empresa o se declararon al firmar.' },
        { title: 'Rol en el documento', text: 'Cargador, transportista o destinatario.' },
        { title: 'Fecha y hora', text: 'El momento exacto del acto de firma.' },
        { title: 'Contexto', text: 'Ubicación, zona horaria y dispositivo.' },
        { title: 'Huella criptográfica', text: 'Del ejemplar exacto que se firmó.' },
        { title: 'Aceptación expresa', text: 'El consentimiento del acto de firma.' },
      ],
    },
    biometricNote: 'La biometría no se usa para identificar a nadie de forma automática: es evidencia técnica de la autoría y la voluntad de quien firma.',
    keep: {
      eyebrow: 'Conservación',
      title: 'Exactamente el documento que se firmó, durante años',
      text: 'Al completarse, cada firma se incorpora al PDF en formato PAdES con validación a largo plazo (LTV). Con él se conservan sus versiones, las evidencias de cada firma, el registro de eventos y los sellos de tiempo: todo lo necesario para reconstruir y probar lo que pasó en cada transporte.',
      cols: 4,
      items: [
        { title: 'Inmutable', text: 'El documento firmado no puede modificarse sin invalidar sus evidencias.' },
        { title: 'Alteraciones detectables', text: 'Cualquier cambio posterior se detecta con una validación criptográfica.' },
        { title: 'Evidencias vinculadas', text: 'Cada evidencia apunta, por su huella criptográfica, a una versión concreta del documento.' },
        { title: 'Verificable a largo plazo', text: 'El sellado de tiempo y la validación LTV permiten verificar la firma aunque hayan caducado los certificados.' },
      ],
    },
    archiveEyebrow: 'Archivo',
    archiveTitle: 'Cerrado a cambios, a mano cuando lo necesite',
    archiveText: 'A los 30 días del transporte, cada documento pasa solo al Archivo, cerrado a cambios y con las mismas evidencias, junto a los documentos de otras empresas que usted haya archivado.',
    archiveItems: ['Carpetas por cliente, transportista, mes de carga, referencia o tipo', 'Búsqueda en todo el archivo', 'Descarga en ZIP para una auditoría o una reclamación'],
    portEyebrow: 'Portabilidad',
    portTitle: 'Sin depender de BlueCMR',
    portText: 'Los documentos y sus evidencias se descargan, se exportan y se pueden trasladar a otros sistemas. El PDF firmado mantiene su capacidad de prueba fuera de la plataforma.',
    portItems: ['Descarga del PDF firmado', 'Exportación de documentos y datos', 'Informe de evidencias de cada documento'],
    disputeEyebrow: 'Si hay una disputa',
    disputeTitle: 'Informe pericial bajo demanda',
    dispute: [
      'Ante una disputa, BlueCMR emite un informe de verificación de cualquier firma: comprueba la integridad de las evidencias, su sello criptográfico, el ejemplar exacto que firmó cada parte y el sello de tiempo. Cada comprobación es reproducible por un perito independiente.',
      'Si un juzgado lo requiere, un procedimiento de custodia con doble control permite recuperar el trazo manuscrito y su dinámica para un análisis pericial, dejando registro de cada acceso.',
      'El informe se entrega en PDF junto con un anexo técnico de reproducibilidad.',
    ],
    accessEyebrow: 'Acceso',
    accessTitle: 'Quién puede ver cada documento',
    access: [
      { title: 'Las partes', text: 'Cargador, transportista, destinatario y otros autorizados acceden al documento, a su estado y a sus evidencias, según los permisos y funciones definidos.' },
      { title: 'Las autoridades', text: 'En una inspección, o cuando lo requieran, el acceso es inmediato y verificable y no modifica el documento original: el agente lo abre con su QR.' },
    ],
    faqEyebrow: 'Preguntas frecuentes',
    faqTitle: 'Sobre la firma y la conservación',
    faq: [
      { q: '¿Tiene validez legal y sirve como prueba?', a: 'Sí. Es una firma electrónica avanzada conforme al artículo 26 del Reglamento eIDAS, y una firma electrónica no puede rechazarse como prueba por el mero hecho de ser electrónica (art. 25.1). Con sus evidencias, el sellado de tiempo y la validación LTV, el documento sirve ante autoridades, auditorías y juzgados.' },
      { q: '¿Quien firma necesita instalar algo o tener cuenta?', a: 'No. Firma desde el navegador del móvil con el enlace que recibe por correo, SMS, WhatsApp o QR.' },
      { q: '¿Hay que firmar el DeCA?', a: 'No. La firma no es requisito de validez del DeCA. Si se firma, debe ser una firma electrónica avanzada o cualificada, como la de BlueCMR.' },
      { q: '¿Cuánto tiempo hay que conservar los documentos?', a: 'Depende del documento y de la norma: la carta de porte, por ejemplo, al menos un año (art. 10 bis.6 de la Ley 15/2009). Definir y respetar el plazo que aplica corresponde a cada parte; BlueCMR conserva los documentos y sus evidencias íntegros y accesibles.' },
    ],
    closing: 'Véalo firmar en directo',
    closingText: 'Una demostración con sus documentos y sus firmantes: cómo se firma y qué queda de cada transporte, sin compromiso.',
    breadcrumb: ['Inicio', 'Confianza', 'Firma y evidencias'],
  },
  en: {
    seoTitle: 'Advanced electronic signature and evidence preservation | BlueCMR',
    seoDescription: 'How transport documents are signed and preserved in BlueCMR: advanced e-signature under eIDAS, evidence of every signature and a PAdES-LTV PDF.',
    seoKeywords: 'advanced electronic signature, transport document signature, eidas, biometric signature, pades-ltv, transport document preservation, electronic evidence',
    heroEyebrow: 'Trust · Signature and evidence',
    heroTitle: 'Signed on a phone, provable years later',
    heroText: 'Every signature in BlueCMR is an advanced electronic signature: it links the signer to the document and to their intent and leaves evidence that proves it. The document is preserved with all of it, intact and verifiable, outside the platform too.',
    heroChecks: ['eIDAS, Article 26', 'PAdES-LTV', 'Exportable'],
    typesEyebrow: 'Legal framework',
    typesTitle: 'The signature BlueCMR uses',
    typesText: 'The eIDAS Regulation (EU 910/2014) distinguishes three types of electronic signature. BlueCMR uses the advanced one, suitable for transport documents under eIDAS, the eCMR Protocol and Spanish law.',
    types: [
      { title: 'Simple', text: 'Any electronic data the signer attaches to other data in order to sign.' },
      { title: 'Advanced', text: 'Uniquely linked to the signer, capable of identifying them, created under their control and making any later change detectable (Art. 26).', mark: 'BlueCMR\'s' },
      { title: 'Qualified', text: 'Advanced, created with a qualified device and based on a qualified certificate; equivalent to a handwritten signature.' },
    ],
    modes: {
      eyebrow: 'Signing methods',
      title: 'How documents are signed',
      cols: 4,
      items: [
        { title: 'Handwritten biometric', text: 'With a finger, on a phone or tablet. The stroke and its dynamics (position, time, speed, pressure and acceleration) are captured and encrypted on the device itself.' },
        { title: 'With an electronic seal', text: 'For the company\'s identified users: one-click signing, with no handwritten stroke.' },
        { title: 'Unattended', text: 'For automated processes, with BlueCMR\'s electronic seal.' },
        { title: 'With OTP verification', text: 'If configured, the signer confirms with a code received by SMS or email: added evidence that they control that channel.' },
      ],
    },
    sendEyebrow: 'Sending for signature',
    sendTitle: 'Signers need no account',
    send: [
      { title: 'Who signs', text: 'Drivers, second drivers, transport operators, consignees and other parties, each from their own device.', items: ['On the issuer\'s own device', 'Or with the document sent to each signer', 'Nothing to install, no sign-up'] },
      { title: 'Each signing link', text: 'Arrives by email, SMS, WhatsApp or QR code and:', items: ['Is tied to a unique token', 'Can be revoked at any time', 'Only allows the authorised signature'] },
    ],
    evidence: {
      eyebrow: 'Evidence',
      title: 'What every signature leaves behind',
      text: 'Each signature generates a set of evidence that makes it possible to reconstruct it and prove it to third parties, authorities or in court proceedings.',
      items: [
        { title: 'Signer identity', text: 'Name, email and ID document, recording whether they come from the company profile or were declared when signing.' },
        { title: 'Role in the document', text: 'Shipper, carrier or consignee.' },
        { title: 'Date and time', text: 'The exact moment of signing.' },
        { title: 'Context', text: 'Location, time zone and device.' },
        { title: 'Cryptographic hash', text: 'Of the exact copy that was signed.' },
        { title: 'Express acceptance', text: 'Consent to the act of signing.' },
      ],
    },
    biometricNote: 'Biometrics are not used to identify anyone automatically: they are technical evidence of the signer\'s authorship and intent.',
    keep: {
      eyebrow: 'Preservation',
      title: 'Exactly the document that was signed, for years',
      text: 'Once completed, each signature is embedded in the PDF in PAdES format with long-term validation (LTV). Its versions, the evidence of every signature, the event log and the timestamps are preserved with it: everything needed to reconstruct and prove what happened in each transport.',
      cols: 4,
      items: [
        { title: 'Immutable', text: 'The signed document cannot be modified without invalidating its evidence.' },
        { title: 'Detectable alterations', text: 'Any later change is detected by a cryptographic validation.' },
        { title: 'Linked evidence', text: 'Each piece of evidence points, through its cryptographic hash, to a specific version of the document.' },
        { title: 'Verifiable long-term', text: 'Timestamping and LTV validation allow the signature to be verified even after the certificates have expired.' },
      ],
    },
    archiveEyebrow: 'Archive',
    archiveTitle: 'Closed to changes, at hand when you need it',
    archiveText: '30 days after transport, every document moves to the Archive on its own, closed to changes and with the same evidence, alongside the documents from other companies you have archived.',
    archiveItems: ['Folders by customer, carrier, loading month, reference or type', 'Search across the whole archive', 'ZIP download for an audit or a claim'],
    portEyebrow: 'Portability',
    portTitle: 'No dependence on BlueCMR',
    portText: 'Documents and their evidence can be downloaded, exported and moved to other systems. The signed PDF keeps its value as evidence outside the platform.',
    portItems: ['Download of the signed PDF', 'Export of documents and data', 'Evidence report for every document'],
    disputeEyebrow: 'In a dispute',
    disputeTitle: 'Expert report on request',
    dispute: [
      'In the event of a dispute, BlueCMR issues a verification report for any signature: it checks the integrity of the evidence, its cryptographic seal, the exact copy each party signed and the timestamp. Every check can be reproduced by an independent expert.',
      'If a court requires it, a dual-control custody procedure allows the handwritten stroke and its dynamics to be recovered for expert analysis, with every access recorded.',
      'The report is delivered as a PDF together with a technical reproducibility annex.',
    ],
    accessEyebrow: 'Access',
    accessTitle: 'Who can see each document',
    access: [
      { title: 'The parties', text: 'Shipper, carrier, consignee and other authorised parties access the document, its status and its evidence, according to the permissions and roles defined.' },
      { title: 'The authorities', text: 'At a roadside check, or whenever required, access is immediate and verifiable and does not modify the original document: the officer opens it with its QR code.' },
    ],
    faqEyebrow: 'FAQ',
    faqTitle: 'About signing and preservation',
    faq: [
      { q: 'Is it legally valid and usable as evidence?', a: 'Yes. It is an advanced electronic signature under Article 26 of the eIDAS Regulation, and an electronic signature cannot be denied as evidence solely because it is electronic (Art. 25(1)). With its evidence, timestamping and LTV validation, the document can be used before authorities, in audits and in court.' },
      { q: 'Does the signer need to install anything or have an account?', a: 'No. They sign from their phone\'s browser with the link received by email, SMS, WhatsApp or QR code.' },
      { q: 'How long must documents be kept?', a: 'It depends on the document and the applicable rules. Defining and meeting the retention period that applies is up to each party; BlueCMR keeps documents and their evidence intact and accessible.' },
    ],
    closing: 'See it signing live',
    closingText: 'A demonstration with your documents and your signers: how signing works and what remains of each transport, with no commitment.',
    breadcrumb: ['Home', 'Trust', 'Signature and evidence'],
  },
};

const FirmaElectronicaConfianzaPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const c = COPY[lang];
  const { getLocalizedUrl } = useLocalizedUrl();
  const primaryCta = usePrimaryCta(lang);

  return (
    <>
      <SeoMeta
        title={c.seoTitle}
        description={c.seoDescription}
        canonical={PAGE_URL[lang]}
        type="website"
        alternates={{ es: PAGE_URL.es, en: PAGE_URL.en, 'x-default': PAGE_URL.es }}
        keywords={c.seoKeywords}
        breadcrumbs={[
          { name: c.breadcrumb[0], url: lang === 'es' ? 'https://www.bluecmr.com/' : 'https://www.bluecmr.com/en' },
          { name: c.breadcrumb[2], url: PAGE_URL[lang] },
        ]}
        structuredData={[faqSchema(c.faq)]}
      />

      <div className="bg-white">
        <PageHero eyebrow={c.heroEyebrow} title={c.heroTitle} text={c.heroText} image={HERO_IMAGE} primary={primaryCta} compact>
          <HeroChecks items={c.heroChecks} />
        </PageHero>

        {/* Tipos de firma (eIDAS) */}
        <Section tone="white">
          <SectionIntro eyebrow={c.typesEyebrow} title={c.typesTitle} text={c.typesText} />
          <div className="grid md:grid-cols-3 gap-x-10 gap-y-8">
            {c.types.map((t) => (
              <div key={t.title} className={t.mark ? 'border-t-2 border-primary-700 pt-6' : 'border-t border-slate-300 pt-6'}>
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className={`font-semibold ${t.mark ? 'text-primary-700' : 'text-slate-900'}`}>{t.title}</h3>
                  {t.mark && <span className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-700">{t.mark}</span>}
                </div>
                <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">{t.text}</p>
              </div>
            ))}
          </div>
        </Section>

        <ItemsSection {...c.modes} tone="gray" />

        <Section tone="white">
          <SectionIntro eyebrow={c.sendEyebrow} title={c.sendTitle} />
          <TwoColumns cols={c.send} />
        </Section>

        <Section tone="gray">
          <SectionIntro eyebrow={c.evidence.eyebrow} title={c.evidence.title} text={c.evidence.text} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
            {c.evidence.items.map((item) => (
              <div key={item.title} className="border-t border-slate-300 pt-6">
                <h3 className="font-semibold text-slate-900">{item.title}</h3>
                <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-12 max-w-3xl text-slate-700 leading-relaxed">{c.biometricNote}</p>
        </Section>

        {/* Conservación: PDF PAdES-LTV e integridad (ancla para los enlaces de otras páginas) */}
        <div id="conservacion" className="scroll-mt-24">
          <ItemsSection {...c.keep} tone="white" />
        </div>

        {/* Archivo y portabilidad */}
        <Section tone="gray">
          <div className="grid lg:grid-cols-2 gap-x-16 gap-y-14">
            <div>
              <SectionIntro eyebrow={c.archiveEyebrow} title={c.archiveTitle} className="mb-6" />
              <p className="text-slate-600 leading-relaxed">{c.archiveText}</p>
              <CheckList items={c.archiveItems} className="mt-6 space-y-2.5" />
            </div>
            <div>
              <SectionIntro eyebrow={c.portEyebrow} title={c.portTitle} className="mb-6" />
              <p className="text-slate-600 leading-relaxed">{c.portText}</p>
              <CheckList items={c.portItems} className="mt-6 space-y-2.5" />
            </div>
          </div>
        </Section>

        {/* Informe pericial y acceso */}
        <Section tone="white">
          <div className="grid lg:grid-cols-2 gap-x-16 gap-y-14">
            <div>
              <SectionIntro eyebrow={c.disputeEyebrow} title={c.disputeTitle} className="mb-6" />
              <div className="space-y-4 text-slate-600 leading-relaxed">
                {c.dispute.map((p) => <p key={p}>{p}</p>)}
              </div>
            </div>
            <div>
              <SectionIntro eyebrow={c.accessEyebrow} title={c.accessTitle} className="mb-6" />
              <div className="space-y-6">
                {c.access.map((a) => (
                  <div key={a.title} className="border-t border-slate-300 pt-5">
                    <h3 className="font-semibold text-slate-900">{a.title}</h3>
                    <p className="mt-2 text-slate-600 leading-relaxed">{a.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        <FaqSection eyebrow={c.faqEyebrow} title={c.faqTitle} items={c.faq} tone="gray" />
        <RelatedSection title={RELATED_TITLE[lang]} items={otherTrustPages(lang, 'firma', getLocalizedUrl)} />

        <ClosingCta title={c.closing} text={c.closingText} lang={lang} />
      </div>
    </>
  );
};

export default FirmaElectronicaConfianzaPage;
